import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield/text-field';
import Button from '@atlaskit/button/default/button';
import Form from '@atlaskit/form/form';

import { ADVANCED_SEARCH } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { useContentType } from '@confluence/page-context';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { i18n } from './pagetreeSearchMacroI18n.ts';
import { getRootPageParam } from './pageTreeSearchMacroParams.ts';
import { useResolveAncestorId } from './hooks/useResolveAncestorId.ts';

type MacroParamValue = { value?: string };

type PageTreeSearchMacroParams = {
	rootPage?: MacroParamValue;
	rootpage?: MacroParamValue;
	'0'?: MacroParamValue;
};

export type PageTreeSearchMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: PageTreeSearchMacroParams;
};

const styles = cssMap({
	searchInput: {
		width: '165px',
		marginRight: token('space.100'),
	},
});

const ERROR_MESSAGES: Record<string, typeof i18n.rootSpaceNotFound> = {
	rootSpaceNotFound: i18n.rootSpaceNotFound,
	rootPageNotFound: i18n.rootPageNotFound,
	invalidPageContext: i18n.invalidPageContext,
};

const PageTreeSearchMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: PageTreeSearchMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);
	const [contentType] = useContentType();
	const { push } = useRouteActions();

	const { spaceKey, ancestorId, loading, errors } = useResolveAncestorId({
		rootPageString: getRootPageParam(macroParams),
		defaultSpaceKey: macroDefaultProps.spaceKey ?? '',
		contentId: macroDefaultProps.contentId,
		contentType,
	});

	const [searchQuery, setSearchQuery] = useState('');

	const handleSearchChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value),
		[],
	);

	const handleSearchSubmit = useCallback(() => {
		const trimmed = searchQuery.trim();
		if (!trimmed) return;
		const queryParams: Record<string, string> = { text: trimmed };
		if (spaceKey) queryParams.spaces = spaceKey;
		if (ancestorId) queryParams.ancestor = ancestorId;
		push(ADVANCED_SEARCH.toUrl({}, { query: queryParams }));
	}, [push, searchQuery, spaceKey, ancestorId]);

	const isEditorMode = macroDefaultProps.mode === 'editor';
	const hasErrors = errors.length > 0;
	const isSubmitDisabled = hasErrors || loading || isEditorMode;
	const trackingError = hasErrors
		? new Error(
				errors
					.map((errorKey) => {
						const msg = ERROR_MESSAGES[errorKey];
						return msg ? formatMessage(msg) : errorKey;
					})
					.join(', '),
			)
		: null;

	return (
		<Box
			testId="pagetree-search-macro"
			data-fabric-macro={macroDefaultProps?.attributes?.macroId}
			data-macro-body=""
		>
			{!loading && trackingError && (
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={trackingError}
				/>
			)}
			{!loading && !hasErrors && (
				<MacroExperienceSuccess
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
				/>
			)}
			{hasErrors && (
				<Box testId="pagetree-search-error">
					{errors.map((errorKey) => {
						const msg = ERROR_MESSAGES[errorKey];
						return msg ? (
							<Box key={errorKey}>
								<FormattedMessage {...msg} />
							</Box>
						) : null;
					})}
				</Box>
			)}

			<Form onSubmit={handleSearchSubmit}>
				<Inline alignBlock="center">
					<Box xcss={styles.searchInput}>
						<Textfield
							value={searchQuery}
							onChange={handleSearchChange}
							name="queryString"
							isDisabled={isEditorMode}
							testId="pagetree-search-input"
							aria-label={formatMessage(i18n.searchButtonText)}
						/>
					</Box>
					<Button
						appearance="default"
						type="submit"
						isDisabled={isSubmitDisabled}
						testId="pagetree-search-button"
					>
						{formatMessage(i18n.searchButtonText)}
					</Button>
				</Inline>
			</Form>
		</Box>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

export const PageTreeSearchMacroRenderer = (
	props: PageTreeSearchMacroRendererProps,
): React.JSX.Element => (
	<GenericErrorBoundary
		renderOnError={renderErrorFallback}
		attribution={Attribution.PAGE_EXTENSIONS}
	>
		<PageTreeSearchMacroRendererComponent {...props} />
	</GenericErrorBoundary>
);
